/**
 * アルコール判定の閾値（画面表示用）。
 *
 * 正となる値はアラート設定（alert_settings の 'alcohol_warning'）で、打刻時の判定にも
 * アラート生成にも同じ値が使われる。管理サイトの一覧・詳細で「正常／要確認／超過」を
 * 出すために GET /alert-settings/alcohol から取得する。
 *
 * 閾値は会社ごとに変えられるため、画面側に数値を直接書かないこと。
 * 以前は 0.015 / 0.150 が各画面にハードコードされており、設定を変えても表示だけ
 * 元の値のままだった。
 */
import { useEffect, useState } from 'react';
import * as api from '../services/api';

export interface AlcoholThresholds {
  /** これ以上で「要確認」 */
  warning_mgl: number;
  /** これ以上で「基準超過」 */
  critical_mgl: number;
}

/** サーバーから取れないときの既定値（アラート設定の初期値と同じ） */
export const DEFAULT_ALCOHOL_THRESHOLDS: AlcoholThresholds = {
  warning_mgl:  0.015,
  critical_mgl: 0.150,
};

export type AlcoholJudgement = 'normal' | 'warning' | 'critical';

/** 測定値の判定。未測定(null)は判定なし */
export function judgeAlcohol(mgL: number, t: AlcoholThresholds): AlcoholJudgement {
  if (mgL >= t.critical_mgl) return 'critical';
  if (mgL >= t.warning_mgl)  return 'warning';
  return 'normal';
}

/** 「0.015 mg/L」のような表示用文字列 */
export function fmtMgL(v: number): string {
  return `${v.toFixed(3)} mg/L`;
}

// 会社ごとに1度取れれば十分なのでモジュール内に持つ（画面をまたいでも再取得しない）
let cached: AlcoholThresholds | null = null;
let inflight: Promise<AlcoholThresholds> | null = null;

function fetchThresholds(): Promise<AlcoholThresholds> {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = api.get<{ data: AlcoholThresholds }>('/alert-settings/alcohol')
      .then(r => {
        const d = r?.data;
        if (typeof d?.warning_mgl !== 'number' || typeof d?.critical_mgl !== 'number') {
          return DEFAULT_ALCOHOL_THRESHOLDS;
        }
        cached = { warning_mgl: d.warning_mgl, critical_mgl: d.critical_mgl };
        return cached;
      })
      .catch(() => DEFAULT_ALCOHOL_THRESHOLDS)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** ログアウト時など、会社が変わり得るタイミングで捨てる */
export function clearAlcoholThresholdsCache(): void {
  cached = null;
}

/** 取得できるまでは既定値を返す */
export function useAlcoholThresholds(): AlcoholThresholds {
  const [t, setT] = useState<AlcoholThresholds>(cached ?? DEFAULT_ALCOHOL_THRESHOLDS);
  useEffect(() => {
    let cancelled = false;
    fetchThresholds().then(v => { if (!cancelled) setT(v); });
    return () => { cancelled = true; };
  }, []);
  return t;
}
